import { Feature, Polygon, Properties } from '@turf/helpers';

import { LngLat, LngLatBounds } from 'mapbox-gl';

import Poland from 'base/geojson/Poland.json';
import Romania from 'base/geojson/Romania.json';
import Hungary from 'base/geojson/Hungary.json';
import Brazil from 'base/geojson/Brazil.json';
import Bulgaria from 'base/geojson/Bulgaria.json';
import Germany from 'base/geojson/Germany.json';
import Italy from 'base/geojson/Italy.json';
import Ukraine from 'base/geojson/Ukraine.json';
import SouthAfrica from 'base/geojson/South_Africa.json';
import Belarus from 'base/geojson/Belarus.json';
import Russia from 'base/geojson/Russia.json';
import Serbia from 'base/geojson/Serbia.json';
import Portugal from 'base/geojson/Portugal.json';
import Spain from 'base/geojson/Spain.json';
import Kazakhstan from 'base/geojson/Kazakhstan.json';
import England from 'base/geojson/England.json';
import { Field } from 'context/store/recommendationFormReducer';
import { MapsConstants } from 'utils/constants/MapsConstants';

const TARGET_COUNTRIES = [Poland, Romania, Hungary, Bulgaria, Serbia, Belarus];
const TARGET_COUNTRIES_MULTIPOLYGON = [
  Brazil,
  Germany,
  Ukraine,
  Italy,
  SouthAfrica,
  Russia,
  Portugal,
  England,
];
const COUNTRIES_WITH_MULTIPOLYGON = [
  'BRA',
  'DEU',
  'UKR',
  'ITA',
  'ZAF',
  'BRZ',
  'RUS',
  'PRT',
  'ESP',
  'KAZ',
  'GBR',
];

export enum TARGET_COUNTRIES_CODES {
  POL = 'POL',
  ROU = 'ROU',
  HUN = 'HUN',
  BRZ = 'BRZ',
  DEU = 'DEU',
  UKR = 'UKR',
  BRA = 'BRA',
  ITA = 'ITA',
  BGR = 'BGR',
  ZAF = 'ZAF',
  BLR = 'BLR',
  RUS = 'RUS',
  SRB = 'SRB',
  PRT = 'PRT',
  ESP = 'ESP',
  KAZ = 'KAZ',
  GBR = 'GBR',
  FRA = 'FRA',
}
export interface PointCoordinates {
  lng: number;
  lat: number;
}
const turf = (window as any).turf;

/**
 * Validates whether a turf.point is inside a bounding box
 * @param  {LngLat} mapboxPoint Point coordinates
 * @param  {LngLatBounds} mapboxBbox Map bounding box
 * @return {boolean} Returns true if turf.point is within the bounding box
 */
export const pointInBbox = (mapboxPoint: LngLat, mapboxBbox: LngLatBounds): boolean => {
  const northEast = mapboxBbox.getNorthEast();
  const southWest = mapboxBbox.getSouthWest();

  const line = turf.lineString([
    [southWest.lng, southWest.lat],
    [northEast.lng, northEast.lat],
  ]);
  const turfBbox = turf.bbox(line);

  const turfPoint = turf.point([mapboxPoint.lng, mapboxPoint.lat]);
  const turfPolygon = turf.bboxPolygon(turfBbox);

  return turf.booleanPointInPolygon(turfPoint, turfPolygon);
};

/**
 * Sending the coordinates of map viewable in the screen
 * @param  {LngLatBounds} mapboxBbox Map bounding box
 * @return {Polygon} Returns coordinates of a biggest turf.polygon present on the screen
 */

export const currentMapCoordinates = (mapboxBbox: LngLatBounds): Feature<Polygon, Properties> => {
  const northEast = mapboxBbox.getNorthEast();
  const southWest = mapboxBbox.getSouthWest();
  const line = turf.lineString([
    [southWest.lng, southWest.lat],
    [northEast.lng, northEast.lat],
  ]);
  const turfBbox = turf.bbox(line);
  const turfPolygon = turf.bboxPolygon(turfBbox);
  return turfPolygon;
};

/**
 * Validates whether a turf.point is inside any Target Country
 * @param  {PointCoordinates} object {lng,lat} Point coordinates
 * @return {string} Returns Country Code if turf.point is inside a target country
 */

export const isCountryFlagEnabled = (
  code: string | undefined,
  flags: any
): boolean | undefined => {
  const countryEnableData = [
    { countryCode: 'ROU', isEnabled: true },
    { countryCode: 'DEU', isEnabled: true },
    { countryCode: 'ITA', isEnabled: true },
    { countryCode: 'HUN', isEnabled: true },
    { countryCode: 'POL', isEnabled: true },
    { countryCode: 'BRZ', isEnabled: true },
    { countryCode: 'BGR', isEnabled: true },
    { countryCode: 'UKR', isEnabled: true },
    { countryCode: 'ZAF', isEnabled: true },
    { countryCode: 'GBR', isEnabled: true },
  ];
  return countryEnableData?.find((country) => country.countryCode === code)?.isEnabled;
};

export const pointInCountry = (
  { lng, lat }: PointCoordinates,
  flags: any,
  isDetectField = false
): string | undefined => {
  const turfPoint = turf.point([lng, lat]);
  let countryName: string | undefined;

  TARGET_COUNTRIES.some((country) => {
    const countryPolygon = turf.polygon(country.geometry.coordinates);
    if (turf.booleanPointInPolygon(turfPoint, countryPolygon)) {
      countryName = country.properties.sov_a3;
      return true;
    }
  });
  TARGET_COUNTRIES_MULTIPOLYGON.some((country) => {
    const countryMultiPolygon = turf.multiPolygon(country.geometry.coordinates);
    const countryObj = turf.pointsWithinPolygon(turfPoint, countryMultiPolygon);
    if (countryObj?.features.length > 0) {
      countryName = country.properties.sov_a3;
    }
  });
  if (isDetectField || isCountryFlagEnabled(countryName, flags)) {
    return countryName;
  }
  console.log('countryName pointInCountry', countryName);
  return undefined;
};

/**
 * Validates whether a turf.point belongs to specific Target Country
 * @param  {PointCoordinates} object {lng,lat} Point coordinates
 * @param  {string} countryCode country code
 * @return {boolean} Returns a boolean that tell us if the turf.point belongs to the country
 */

export const pointIsInSpecificCountry = ({ lng, lat }: PointCoordinates, countryCode: string) => {
  const turfPoint = turf.point([lng, lat]);

  switch (countryCode) {
    case TARGET_COUNTRIES_CODES.POL:
      return turf.booleanPointInPolygon(turfPoint, turf.polygon(Poland.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.ROU:
      return turf.booleanPointInPolygon(turfPoint, turf.polygon(Romania.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.HUN:
      return turf.booleanPointInPolygon(turfPoint, turf.polygon(Hungary.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.BRZ:
      return turf.booleanPointInPolygon(turfPoint, turf.multiPolygon(Brazil.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.DEU:
      return turf.booleanPointInPolygon(turfPoint, turf.multiPolygon(Germany.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.ITA:
      return turf.booleanPointInPolygon(turfPoint, turf.multiPolygon(Italy.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.BGR:
      return turf.booleanPointInPolygon(turfPoint, turf.polygon(Bulgaria.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.UKR:
      return turf.booleanPointInPolygon(turfPoint, turf.multiPolygon(Ukraine.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.ZAF:
      return turf.booleanPointInPolygon(turfPoint, turf.multiPolygon(SouthAfrica.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.BLR:
      return turf.booleanPointInPolygon(turfPoint, turf.polygon(Belarus.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.RUS:
      return turf.booleanPointInPolygon(turfPoint, turf.multiPolygon(Russia.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.SRB:
      return turf.booleanPointInPolygon(turfPoint, turf.polygon(Serbia.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.PRT:
      return turf.booleanPointInPolygon(turfPoint, turf.multiPolygon(Portugal.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.ESP:
      return turf.booleanPointInPolygon(turfPoint, turf.multiPolygon(Spain.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.KAZ:
      return turf.booleanPointInPolygon(turfPoint, turf.multiPolygon(Kazakhstan.geometry.coordinates));
    case TARGET_COUNTRIES_CODES.GBR:
      return turf.booleanPointInPolygon(turfPoint, turf.multiPolygon(England.geometry.coordinates));
    default:
      return false;
  }
};

/**
 * Validates whether a turf.polygon is inside any Target Country
 * @param  {PointCoordinates} polygonCordinates Polygon well closed coordinates
 * @return {boolean}  Returns true if whole turf.polygon is within any Target country
 */
export const polygonInsideCountry = (
  polygonCordinates: Array<[number, number]>,
  countryCode: string | undefined,
  isBrazilEnable: boolean
) => {
  const drawnPolygon = turf.polygon([[...polygonCordinates]]);
  const isWithinCountryBoundary: boolean[] = [];

  TARGET_COUNTRIES.forEach((country) => {
    const countryPolygon = turf.polygon(country.geometry.coordinates);
    isWithinCountryBoundary.push(turf.booleanWithin(drawnPolygon, countryPolygon));
  });
  TARGET_COUNTRIES_MULTIPOLYGON.forEach((country) => {
    const countryMultiPolygon = turf.multiPolygon(country.geometry.coordinates);
    isWithinCountryBoundary.push(turf.booleanWithin(drawnPolygon, countryMultiPolygon));
  });
  return isWithinCountryBoundary?.some((item) => item);
};

/**
 * Validates whether two lines are overlapping each other
 * @param  {[number, number][]} oldLine First line coordinates
 * @param  {[number, number][]} newLine New line coordinates
 * @return {boolean}  Returns true if lines are intersecting MORE than once
 */
export const isLineStringOverlaping = ({
  oldLine,
  newLine,
}: {
  oldLine: [number, number][];
  newLine: [number, number][];
}) => {
  const currentPolyline = turf.lineString(oldLine);
  const newPolyline = turf.lineString(newLine);
  const areIntersectsBetweenPolylines = turf.lineIntersect(currentPolyline, newPolyline);
  return areIntersectsBetweenPolylines.features.length > 1;
};

/**
 * Validates whether a turf.polygon is SELF intersecting
 * @param  {Array<[number, number]>} polygonCoordinates Polygon well closed
 * @return {boolean}  Returns true if turf.polygon is self intersecting
 */
export const isPolygonOverlaping = ({
  polygonCoordinates,
}: {
  polygonCoordinates: Array<[number, number]>;
}) => {
  const { features } = turf.kinks(turf.polygon([polygonCoordinates]));
  return features.length !== 0;
};

/**
 * Validates whether a turf.point is inside any provided polygons
 * @param  {PointCoordinates} object {lng,lat} Point coordinates
 * @param  {Field[]} polygonArray Polygons well closed
 * @return {boolean}  Returns true if turf.point is inside any provided polygons
 */
export const pointInPolygons = ({ lng, lat }: PointCoordinates, polygonArray: Field[]) => {
  const turfPoint = turf.point([lng, lat]);

  return polygonArray.some((polygonItem) => {
    const turfPolygon = turf.polygon([...polygonItem.boundary.geometry.coordinates]);
    return turf.booleanPointInPolygon(turfPoint, turfPolygon);
  });
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const findFieldToEdit = ({ lng, lat }: PointCoordinates, polygonArray: Field[]) => {
  const turfPoint = turf.point([lng, lat]);
  return polygonArray.find((polygonItem) => {
    const turfPolygon = turf.polygon([...polygonItem.boundary.geometry.coordinates]);
    return turf.booleanPointInPolygon(turfPoint, turfPolygon);
  });
};

/**
 * Validates whether a line is intersecting another provided turf.polygon
 * @param  {[number, number][]} line Line coordinates
 * @param  {Field[]} polygonArray Polygons well closed coordinates
 * @return {boolean}  Returns true if line intersects any turf.polygon
 */
export const isLineOverlappingPolygons = ({
  line,
  polygonArray,
}: {
  line: [number, number][];
  polygonArray: Field[];
}) => {
  const currentPolyline = turf.lineString(line);

  return polygonArray.some((polygonItem) => {
    const turfPolygon = turf.polygon([...polygonItem.boundary.geometry.coordinates]);
    const intersection = turf.lineIntersect(currentPolyline, turfPolygon);
    return intersection.features.length > 0;
  });
};

/**
 * Validates whether a turf.polygon is contained/within or intersects another provided turf.polygon
 * @param  {Array<[number, number]>} polygonCordinates Polygon well closed
 * @param  {Field[]} polygonArray Polygons well closed
 * @return {boolean}  Returns true if any provided turf.polygon is contained or intersects polygonCordinates
 */
export const polygonsOverlaps = (
  polygonCordinates: Array<[number, number]>,
  polygonArray: Field[]
) => {
  const drawnPolygon = turf.polygon([[...polygonCordinates]]);

  return polygonArray.some((polygonItem) => {
    const turfPolygon = turf.polygon(polygonItem.boundary.geometry.coordinates);
    const resultWithin = turf.booleanWithin(turfPolygon, drawnPolygon);
    const resultIntersect = turf.intersect(turfPolygon, drawnPolygon);
    return resultWithin || resultIntersect !== null;
  });
};

/**
 * @param  {Array<[number, number]>} polygonCordinates Polygon to be calculated it's turf.area
 * * @return { number }  Returns the turf.area in hectares rouned by three decimals
 */
export const calculateAreaOfPolygon = (polygonCordinates: Array<[number, number]>) => {
  const drawnPolygon = turf.polygon([[...polygonCordinates]]);
  const squareMetters = turf.area(drawnPolygon);
  const hectares = squareMetters / 10000;

  return hectares.toFixed(3);
};

export const getCountryPostalCode = (countryCode: string | undefined): string => {
  if (!countryCode) {
    return '';
  }
  let postalCode: string;
  if (COUNTRIES_WITH_MULTIPOLYGON.includes(countryCode)) {
    postalCode =
      TARGET_COUNTRIES_MULTIPOLYGON?.find((data) => data.properties.sov_a3 === countryCode)
        ?.properties.postal ?? '';
  } else {
    postalCode =
      TARGET_COUNTRIES?.find((data) => data.properties.sov_a3 === countryCode)?.properties.postal ??
      '';
  }
  return postalCode?.toLocaleLowerCase();
};

export const getCentroidByCountry = (countryCode: string) => {
  let countryCentroid;
  switch (countryCode) {
    case TARGET_COUNTRIES_CODES.POL:
      countryCentroid = turf.centroid(turf.polygon(Poland.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.ROU:
      countryCentroid = turf.centroid(turf.polygon(Romania.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.HUN:
      countryCentroid = turf.centroid(turf.polygon(Hungary.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.BRA:
      countryCentroid = turf.centroid(turf.multiPolygon(Brazil.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.DEU:
      countryCentroid = turf.centroid(turf.multiPolygon(Germany.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.ITA:
      countryCentroid = turf.centroid(turf.multiPolygon(Italy.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.BGR:
      countryCentroid = turf.centroid(turf.polygon(Bulgaria.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.UKR:
      countryCentroid = turf.centroid(turf.multiPolygon(Ukraine.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.ZAF:
      countryCentroid = turf.centroid(turf.multiPolygon(SouthAfrica.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.BLR:
      countryCentroid = turf.centroid(turf.polygon(Belarus.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.RUS:
      countryCentroid = turf.centroid(turf.multiPolygon(Russia.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.SRB:
      countryCentroid = turf.centroid(turf.polygon(Serbia.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.PRT:
      countryCentroid = turf.centroid(turf.multiPolygon(Portugal.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.ESP:
      countryCentroid = turf.centroid(turf.multiPolygon(Spain.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.KAZ:
      countryCentroid = turf.centroid(turf.multiPolygon(Kazakhstan.geometry.coordinates));
      break;
    case TARGET_COUNTRIES_CODES.GBR:
      countryCentroid = turf.centroid(turf.multiPolygon(England.geometry.coordinates));
      break;
    case 'FRA':
      countryCentroid = { geometry: { coordinates: [2.3522, 48.8566] } };
      break;
    case 'NLD':
      countryCentroid = { geometry: { coordinates: [4.9041, 52.3676] } };
      break;
    case 'BEL':
      countryCentroid = { geometry: { coordinates: [4.3517, 50.8503] } };
      break;
    case 'MDA':
      countryCentroid = { geometry: { coordinates: [28.8638, 47.0105] } };
      break;
    case 'CHE':
      countryCentroid = { geometry: { coordinates: [7.4474, 46.9480] } }; // Bern
      break;
    default:
      countryCentroid = turf.centroid(turf.polygon(Poland.geometry.coordinates));
  }
  return countryCentroid;
};

export const getZoomLevelByCountry = (countryCode: string) => {
  let countryZoom;
  switch (countryCode) {
    case TARGET_COUNTRIES_CODES.POL:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_POLAND;
      break;
    case TARGET_COUNTRIES_CODES.ROU:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_ROMANIA;
      break;
    case TARGET_COUNTRIES_CODES.HUN:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_HUNGARY;
      break;
    case TARGET_COUNTRIES_CODES.BRA:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_BRAZIL;
      break;
    case TARGET_COUNTRIES_CODES.DEU:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_GERMANY;
      break;
    case TARGET_COUNTRIES_CODES.ITA:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_ITALY;
      break;
    case TARGET_COUNTRIES_CODES.BGR:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_BULGARIA;
      break;
    case TARGET_COUNTRIES_CODES.UKR:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_UKRAINE;
      break;
    case TARGET_COUNTRIES_CODES.ZAF:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_SOUTH_AFRICA;
      break;
    case TARGET_COUNTRIES_CODES.GBR:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_ENGLAND;
      break;
    default:
      countryZoom = MapsConstants.ZOOM_IN_LIMIT_POLAND;
  }
  return countryZoom;
};

export const getZoomLevelForLocation = (place: string[]) => {
  if (place.includes('country')) {
    return 7;
  } else if (place.includes('region') || place.includes('place')) {
    return 10;
  }
  return 15;
};
